import { useMemo, useState } from 'react'
import { useFloorplansContext } from './floorplans-context.jsx'
import NorthArrow from '../../../svg/north-arrow.svg'
import ArrowRight from '../../../svg/arrow-right-3.svg'

export default function PlanList() {
  const { bedroomsFilter, planLevelsFilter, activePlan, setActivePlan } = useFloorplansContext()
  const [sortBy, setSortBy] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const plans = floorplanData?.plans || []
  const bedroomOptions = floorplanData?.bedroom_options || []
  const brochurePdf = floorplanData?.brochure_pdf || ''
  const floorplanDisclaimerText = floorplanData?.floorplan_disclaimer_text || ''

  const bedroomLabelMap = useMemo(() => bedroomOptions.reduce((acc, option) => {
    acc[option.value] = {
      label: option.label,
      shortLabel: option.short_label
    }
    return acc
  }, {}), [bedroomOptions])

  const filteredPlans = plans.filter((plan) => {
    const matchesBedrooms = bedroomsFilter === 'all' || plan.bedrooms === bedroomsFilter
    const planLevels = Array.isArray(plan.plan_levels) ? plan.plan_levels : []
    const matchesPlanLevels = planLevelsFilter === 'all' || planLevels.includes(planLevelsFilter)

    return matchesBedrooms && matchesPlanLevels
  })

  const parseSortableNumber = (value) => {
    if (value === null || value === undefined || value === '') {
      return Number.NaN
    }

    if (typeof value === 'number') {
      return value
    }

    const normalized = String(value).replace(/[^\d.-]/g, '')
    return Number(normalized)
  }

  const compareValuesAscending = (a, b) => {
    const aNumber = parseSortableNumber(a)
    const bNumber = parseSortableNumber(b)
    const aIsNumber = Number.isFinite(aNumber)
    const bIsNumber = Number.isFinite(bNumber)

    if (aIsNumber && bIsNumber) {
      return aNumber - bNumber
    }

    return String(a || '').localeCompare(String(b || ''), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  }

  const comparePlanNamesAscending = (a, b) => {
    const normalize = (value) => String(value || '')
      .toLowerCase()
      .replace(/plan\s+/g, '')
      .replace(/\s+/g, '')

    const tokenize = (value) => normalize(value)
      .split('/')
      .filter(Boolean)
      .map((token) => {
        const match = token.match(/^(\d+)([a-z]+)?$/i)

        if (match) {
          return {
            number: Number(match[1]),
            suffix: match[2] || ''
          }
        }

        return {
          number: Number.MAX_SAFE_INTEGER,
          suffix: token
        }
      })

    const aTokens = tokenize(a)
    const bTokens = tokenize(b)
    const tokenCount = Math.max(aTokens.length, bTokens.length)

    for (let i = 0; i < tokenCount; i += 1) {
      const aToken = aTokens[i]
      const bToken = bTokens[i]

      if (!aToken && bToken) return -1
      if (aToken && !bToken) return 1
      if (!aToken || !bToken) continue

      if (aToken.number !== bToken.number) {
        return aToken.number - bToken.number
      }

      const suffixCompare = aToken.suffix.localeCompare(bToken.suffix, undefined, {
        sensitivity: 'base'
      })

      if (suffixCompare !== 0) {
        return suffixCompare
      }
    }

    return normalize(a).localeCompare(normalize(b), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  }

  const compareBedroomsAscending = (a, b) => {
    const normalizeBedroomLabel = (value) => String(bedroomLabelMap[value]?.label || value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim()

    const getBedroomRank = (value) => {
      const label = normalizeBedroomLabel(value)
      const compactLabel = label.replace(/\s+/g, '')
      const rawValue = String(value || '').toLowerCase().replace(/\s+/g, '')

      if (/^1\s*bed(room)?\b/.test(label) || /^1bed(room)?/.test(compactLabel) || /^1br/.test(rawValue)) return 1
      if (/^2\s*bed(room)?\b/.test(label) || /^2bed(room)?/.test(compactLabel) || /^2br/.test(rawValue)) return 2
      if (label === 'jr.2 bed' || compactLabel === 'jr.2bed' || compactLabel.startsWith('jr.2bedroom') || compactLabel.startsWith('jr2bedroom') || /^jr2br/.test(rawValue)) return 3
      if (/^3\s*bed(room)?\b/.test(label) || /^3bed(room)?/.test(compactLabel) || /^3br/.test(rawValue)) return 4

      // Fallback for unexpected bedroom formats.
      if (/\b1\b/.test(label) && /\bbed(room)?\b/.test(label)) return 1
      if (/\b2\b/.test(label) && /\bbed(room)?\b/.test(label)) return 2
      if (/\b3\b/.test(label) && /\bbed(room)?\b/.test(label)) return 4

      return 99
    }

    const aRank = getBedroomRank(a)
    const bRank = getBedroomRank(b)

    if (aRank !== bRank) {
      return aRank - bRank
    }

    return normalizeBedroomLabel(a).localeCompare(normalizeBedroomLabel(b), undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  }

  const sortedPlans = useMemo(() => {
    const plansToSort = [...filteredPlans]

    plansToSort.sort((a, b) => {
      let comparison = 0

      if (sortBy === 'name') {
        comparison = comparePlanNamesAscending(simplifyPlanName(a.plan_name), simplifyPlanName(b.plan_name))
      }
      if (sortBy === 'bedrooms') {
        comparison = compareBedroomsAscending(a.bedrooms, b.bedrooms)
      }
      if (sortBy === 'interior-sf') {
        comparison = compareValuesAscending(a.interior_sf, b.interior_sf)
      }
      if (sortBy === 'price') {
        comparison = compareValuesAscending(a.starting_price, b.starting_price)
      }

      return sortDirection === 'desc' ? comparison * -1 : comparison
    })

    return plansToSort
  }, [filteredPlans, sortBy, sortDirection, bedroomLabelMap])

  function simplifyPlanName(planName) {
    if (!planName) {
      return '-'
    }

    // If plan name begins with "Plan ", remove it
    if (planName.startsWith('Plan ')) {
      return planName.substring(5)
    }

    return planName
  }

  const formatStartingPrice = (price) => {
    if (!price) {
      return '-'
    }

    return String(price).startsWith('$') ? price : `$${price}`
  }

  const formatStartingPriceCompact = (price) => {
    if (!price) {
      return '-'
    }

    const normalized = String(price).replace(/[$,\s]/g, '')
    const numericPrice = Number(normalized)

    if (!Number.isFinite(numericPrice)) {
      return formatStartingPrice(price)
    }

    if (numericPrice >= 1000000) {
      return `$${(numericPrice / 1000000).toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1')}M`
    }

    if (numericPrice >= 1000) {
      return `$${Math.ceil(numericPrice / 1000)}K`
    }

    return formatStartingPrice(price)
  }

  const handleSortChange = (sortKey) => {
    if (sortBy === sortKey) {
      setSortDirection((currentDirection) => currentDirection === 'asc' ? 'desc' : 'asc')
      return
    }

    setSortBy(sortKey)
    setSortDirection('asc')
  }

  const getHeaderClassName = (baseClassName, sortKey) => `${baseClassName}${sortBy === sortKey ? ' active' : ''}`

  return (
    <div className="plan-list">
      <div className="plan-list-table">
        <div className="plan-list-table-header d-flex">
          <div className={getHeaderClassName('name', 'name')} role="button" tabIndex={0} onClick={() => handleSortChange('name')} onKeyDown={(event) => event.key === 'Enter' && handleSortChange('name')}>
            Type
          </div>
          <div className={getHeaderClassName('bedrooms', 'bedrooms')} role="button" tabIndex={0} onClick={() => handleSortChange('bedrooms')} onKeyDown={(event) => event.key === 'Enter' && handleSortChange('bedrooms')}>
            Bedrooms
          </div>
          <div className={getHeaderClassName('interior-sf', 'interior-sf')} role="button" tabIndex={0} onClick={() => handleSortChange('interior-sf')} onKeyDown={(event) => event.key === 'Enter' && handleSortChange('interior-sf')}>
            Interior SF
          </div>
          <div className={getHeaderClassName('price', 'price')} role="button" tabIndex={0} onClick={() => handleSortChange('price')} onKeyDown={(event) => event.key === 'Enter' && handleSortChange('price')}>
            Starting Price
          </div>
          <div className="arrow-icon"></div>
        </div>
        {sortedPlans.length > 0 ? (
          sortedPlans.map((plan) => (
            <div
              key={plan.plan_name}
              className={activePlan === plan.plan_id ? 'plan active' : 'plan'}
              onClick={() => setActivePlan(activePlan === plan.plan_id ? null : plan.plan_id)}
            >
              <div className="plan-info-row">
                <div className='name'>
                  <span className="d-none d-md-inline">Plan&nbsp;</span>
                  {simplifyPlanName(plan.plan_name)}
                </div>
                <div className='bedrooms'>
                  <span className="d-none d-md-block">{bedroomLabelMap[plan.bedrooms]?.label || plan.bedrooms || '-'}</span>
                  <span className="d-md-none">{bedroomLabelMap[plan.bedrooms]?.shortLabel || plan.bedrooms || '-'}</span>
                </div>
                <div className='interior-sf'>{plan.interior_sf + ' SF' || '-'}</div>
                <div className='price'>
                  <span className="d-none d-md-inline">{formatStartingPrice(plan.starting_price)}</span>
                  <span className="d-md-none">{formatStartingPriceCompact(plan.starting_price)}</span>
                </div>
                <div className="arrow-icon">
                  <ArrowRight />
                </div>
              </div>
              {/* Collapsing Floorplan Pane (Mobile) */}
              <div className="mobile-plan-image-pane d-md-none">
                <div className="plan-image">
                  <img src={plan.plan_image} alt={plan.plan_name} />
                </div>
                <div className="keyplate-image">
                  <img src={plan.key_plates} alt={`${plan.plan_name} keyplate`} />
                </div>
                <div className="north-arrow">
                  <NorthArrow />
                </div>
                <a href={plan.plan_pdf} className="btn btn-primary" target="_blank" rel="noopener noreferrer">Download Plan</a>
                <a href={brochurePdf} className="btn btn-primary" target="_blank" rel="noopener noreferrer">Download Brochure</a>
                {floorplanDisclaimerText && (
                  <div className="floorplan-disclaimer-text fine-print container-fluid">
                    {floorplanDisclaimerText}
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="no-plans">
            {bedroomsFilter === 'all' && planLevelsFilter === 'all'
              ? 'No floorplans available.'
              : 'No floorplans match this filter.'}
          </div>
        )}
      </div>
    </div>
  )
}